import { Rectangle } from 'pixi.js';

import type { Font } from 'fontkit';
import { assertNumber } from '../../validators';
import { Config } from '../../video-data/config';
import { computeAdvanceWidth, computeBoundingBox, createAndCheckGlyphRun } from './fontkitUtils';
import { WordStyle } from './WordStyle';
import { direction, forString } from './Script';
import type { WordElementToken } from './WordElementToken';

type WordElementMetricsSettings = {
    fontUrl?: string;
    wordElementToken?: WordElementToken;
    direction?: typeof Config.TEXT_DIRECTIONS[number];
    boundingBox?: Rectangle;
    advanceWidth?: number;
};

/**
 * The metric building block for text, which represents a string that has a uniform style and can
 * be rendered with the same fontkit font. It corresponds to {@link Luminary.WordElement}.
 *
 */
export class WordElementMetrics {
    classType = 'WordElementMetrics';
    #text: string = '';
    #font: Font = null;
    #fontUrl: string = null;
    #style: WordStyle = null;
    #advanceWidth = 0;
    #wordElementToken: WordElementToken = null;

    /**
     * The internal bounding box's origin is at the text origin (on left-most pen start on the
     * baseline)
     */
    #boundingBox: Rectangle = Rectangle.EMPTY;

    #direction: typeof Config.TEXT_DIRECTIONS[number] = Config.LEFT_TO_RIGHT_TEXT_DIRECTION;

    #x = 0;
    #y = 0;

    /**
     * Create a new word element metrics object for a given text string, which can be rendered
     * with the provided font using the desired style. The first parameters (text, font, style)
     * are not used if a cached WordElementMetrics is given. Be aware that values are copied shallowly
     * when initializing from cached metrics.
     *
     * @param text - the text string the metric represents
     * @param font - the fontkit font used to render the text
     * @param style - the style applied to the text
     * @param settings - settings objects which can contain the following properties: direction, style, boundingBox, advanceWidth
     */
    constructor(text: string, font: Font, style: WordStyle, settings: WordElementMetricsSettings = {}) {
        if (typeof text !== 'string'
            || font === null || typeof font !== 'object'
            || style === null || !(style instanceof WordStyle)) {
            throw new Error('WordElementMetrics was initialized invalid data');
        }
        this.#text = text;
        this.#font = font;
        this.#fontUrl = settings.fontUrl || null;
        this.#style = style;
        this.#wordElementToken = settings.wordElementToken || null;

        if ('direction' in settings) {
            this.#direction = settings.direction;
        }
        else {
            this.#direction = direction(forString(text));
        }

        if ('boundingBox' in settings && 'advanceWidth' in settings) {
            // We have enough data to skip computing the metrics
            this.#boundingBox = settings.boundingBox;
            this.#advanceWidth = settings.advanceWidth;
        }
        else if (('boundingBox' in settings) !== ('advanceWidth' in settings)) {
            throw new Error('boundingBox and advanceWidth settings cannot be used individually.');
        }
        else {
            // We compute the basic metric data for this object
            this.#computeMetrics();
        }
    }

    /**
     * The reading direction of the word element.
     */
    get direction(): typeof Config.TEXT_DIRECTIONS[number] {
        return this.#direction;
    }

    /**
     * The advance width of the word element.
     */
    get advanceWidth(): number {
        return this.#advanceWidth;
    }

    /**
     * The horizontal distance from the origin pen position to the left bounding box
     * edge.
     */
    get bearingX(): number {
        // The bounding box is computed at the fontkit text origin
        return this.#boundingBox.x;
    }

    /**
     * The x-height of the font. If the x-height can't be read directly from the font, its
     * estimated as 45% of the font's fontHeight as describe by this article:
     * http://jov.arvojournals.org/article.aspx?articleid=2191906
     */
    get xHeight(): number {
        let xHeight = this.#font.xHeight;
        if (!xHeight || xHeight < 1) {
            xHeight = (this.#font.ascent - this.#font.descent) * 0.45;
        }

        // Map from font units to pixels
        return xHeight / this.#font.unitsPerEm * this.#style.fontSize;
    }

    /**
     * The distance from the bounding box's right edge to the advance width, usually a
     * non-negative number since the bounding box's width is typically less than the advance width.
     */
    get rightBearingX(): number {
        return this.#advanceWidth - this.bearingX - this.#boundingBox.width;
    }

    /**
     * The vertical distance from the baseline to the top of the glyph's bounding box.
     */
    get bearingY(): number {
        // The portion of the bounding box above the origin (baseline)
        return this.#boundingBox.height + this.#boundingBox.y;
    }

    /**
     * The distance from the baseline to the highest grid coordinate used to place an
     * outline point. It is a positive value because of the text grid's orientation.
     */
    get ascent(): number {
        // This is essentially the bearingY but can't be less than 0
        return Math.max(this.bearingY, 0);
    }

    /**
     * The distance from the baseline to the lowest grid coordinate used to place an
     * outline point. It is a negative value because of the text grid's orientation.
     */
    get descent(): number {
        // The bounding box's origin is on the baseline
        return Math.min(this.#boundingBox.y, 0);
    }

    /**
     * The number of characters in the word element.
     */
    get numChars(): number {
        return this.#text.length;
    }

    /**
     * The text in the word element as a single string.
     */
    get text(): string {
        return this.#text;
    }

    /**
     * The fontkit font used to render the text.
     */
    get font(): Font {
        return this.#font;
    }

    /**
     * The font url used to render the text.
     */
    get fontUrl(): string {
        return this.#fontUrl;
    }

    /**
     * The style applied to the text.
     */
    get style(): WordStyle {
        return this.#style;
    }

    /**
     * The WordElementToken which generated this WordElementMetrics.
     */
    get wordElementToken(): WordElementToken {
        return this.#wordElementToken;
    }

    /**
     * The x position of the top left corner of the word element's bounding box.
     */
    get x(): number {
        return this.#x;
    }

    set x(value: number) {
        assertNumber(value);
        this.#x = value;
    }

    /**
     * The y position of the top left corner of the word element's bounding box.
     */
    get y(): number {
        return this.#y;
    }

    set y(value: number) {
        assertNumber(value);
        this.#y = value;
    }

    /**
     * Get the bounding box of the word element. The (x, y) position is the top left corner of
     * the bounding box, and the width/height is rounded up to the nearest whole pixel, since
     * the canvas on which it will be rendered must have dimensions of whole pixels.
     */
    getBounds(): Rectangle {
        const bounds = this.#boundingBox.clone();

        // The internal bounding box is positioned at the fontkit text origin, so the (x, y)
        // position needs to remapped.
        bounds.x = this.#x;
        bounds.y = this.#y;

        // When rendering text the underlying texture is created on a canvas which
        // has to be a multiple of whole pixel values, so replicate that here
        bounds.width = Math.ceil(bounds.width);
        bounds.height = Math.ceil(bounds.height);

        return bounds;
    }

    /**
     * Get the raw bounding box of the word element at the origin.
     */
    get boundingBox(): Rectangle {
        return this.#boundingBox.clone();
    }

    get height(): number {
        return this.#boundingBox.height;
    }

    /**
     * Returns a WordElementToken based on the start and end indices.
     * the WordElementToken can be used to create WordElementMetrics.
     *
     * @param start - zero-based index at which to begin extraction
     * @param end - extracts up to but not including end
     * @returns slicedWordElementToken
     */
    slice(start: number, end: number): WordElementToken {
        // clamp values to bounds of the internal text length
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.numChars);
        const slicedWordElementToken = this.#wordElementToken.slice(clampedStart, clampedEnd);

        return slicedWordElementToken;
    }

    /**
     * Returns the advance width from char <start> index up to but not including char <end>.
     *
     * @param start - is an index, must be an integer
     * @param end - is an index, must be an integer
     */
    partialAdvanceWidth(start: number, end: number): number {
        // clamp values to bounds of the internal text length
        const clampedStart = Math.max(0, start);
        const clampedEnd = Math.min(end, this.numChars);

        if (clampedStart === 0 && clampedEnd === this.numChars) {
            // if selecting the whole word, don't need to recompute the advance width
            return this.#advanceWidth;
        }

        const slicedText = this.#text.slice(clampedStart, clampedEnd);

        const { supported, run } = createAndCheckGlyphRun(slicedText, this.#font);

        if (!supported) {
            return 0;
        }

        return computeAdvanceWidth(slicedText, this.#style, this.#font, run);
    }

    /**
     * Computes and stores the internal metrics for the advance width and bounding box.
     */
    #computeMetrics() {
        const { supported, run } = createAndCheckGlyphRun(this.#text, this.#font);

        if (!supported) {
            return;
        }

        this.#advanceWidth = computeAdvanceWidth(this.#text, this.#style, this.#font, run);
        this.#boundingBox = computeBoundingBox(this.#text, this.#style, this.#font, run);
    }

    /**
     * Reset computed metrics.
     */
    resetMetrics() {
        throw new Error('resetMetrics considered harmful for caching.');
    }
}
